'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface UseHomeGenerationFormFloatingOptions {
  isH5: boolean;
}

/** 悬浮提示词框退出动画时长（ms），需与 tailwind animate-floating-bar-out 的 0.28s 对齐。 */
const DESKTOP_FLOATING_EXIT_MS = 280;
/** 完整提示词框收回动画时长（ms），需与 tailwind animate-floating-expand-out 的 0.26s 对齐。 */
const DESKTOP_EXPAND_EXIT_MS = 260;

export const useHomeGenerationFormFloating = ({ isH5 }: UseHomeGenerationFormFloatingOptions) => {
  const [elementsSelectorOpen, setElementsSelectorOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const [mobileModelOpen, setMobileModelOpen] = useState(false);
  const [mobileMediaOptionsOpen, setMobileMediaOptionsOpen] = useState(false);
  const [mobileFormExpanded, setMobileFormExpanded] = useState(false);
  const [desktopFormFloating, setDesktopFormFloating] = useState(false);
  // 两段式退出：desktopFormFloating 转 false 后先保持挂载播退出动画，动画结束再真正卸载。
  const [desktopFloatingRendered, setDesktopFloatingRendered] = useState(false);
  const [desktopFormExpanded, setDesktopFormExpanded] = useState(false);
  // 两段式收回：desktopFormExpanded 转 false 后先保持挂载播收回动画，动画结束再切回简易条。
  const [desktopExpandRendered, setDesktopExpandRendered] = useState(false);
  const [desktopFormPinnedHeight, setDesktopFormPinnedHeight] = useState<number | null>(null);
  const desktopFormAnchorRef = useRef<HTMLElement>(null);
  const desktopFloatingLayerRef = useRef<HTMLDivElement>(null);
  const plainPromptRef = useRef<HTMLTextAreaElement>(null);
  const multiFramePromptRef = useRef<HTMLTextAreaElement>(null);
  // 面板内（含其 Radix 下拉/弹层）交互后短时间内忽略「滚动收起」：下拉延迟关闭时
  // Radix 把焦点还给面板内触发器，focus() 会程序化滚动 scrollableDiv，不应误判为用户滚动离场。
  const suppressScrollCollapseUntilRef = useRef(0);

  useEffect(() => {
    if (desktopFormFloating) {
      setDesktopFloatingRendered(true);
      return;
    }

    if (!desktopFloatingRendered) {
      return;
    }

    const exitTimer = window.setTimeout(() => {
      setDesktopFloatingRendered(false);
      setDesktopFormExpanded(false);
    }, DESKTOP_FLOATING_EXIT_MS);

    return () => {
      window.clearTimeout(exitTimer);
    };
  }, [desktopFloatingRendered, desktopFormFloating]);

  // 完整提示词框收回：展开时立即挂载，收起后延迟卸载让 floating-expand-out 播完。
  useEffect(() => {
    if (desktopFormExpanded) {
      setDesktopExpandRendered(true);
      return;
    }

    if (!desktopExpandRendered) {
      return;
    }

    const exitTimer = window.setTimeout(() => {
      setDesktopExpandRendered(false);
    }, DESKTOP_EXPAND_EXIT_MS);

    return () => {
      window.clearTimeout(exitTimer);
    };
  }, [desktopExpandRendered, desktopFormExpanded]);

  useEffect(() => {
    if (isH5) {
      setDesktopFormFloating(false);
      setDesktopFormExpanded(false);
      return;
    }

    const scrollElement = document.getElementById('scrollableDiv');
    const getViewportTop = () => scrollElement?.getBoundingClientRect().top ?? 0;

    const syncDesktopFloatingState = () => {
      const formElement = desktopFormAnchorRef.current;
      if (!formElement) return;

      const rect = formElement.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;

      const shouldFloat = rect.bottom <= getViewportTop() + 12;
      setDesktopFormFloating((previous) => (previous === shouldFloat ? previous : shouldFloat));
      // 展开态不在这里收起：留到退出动画结束，避免下滑时先塌回简易条再淡出。
    };

    syncDesktopFloatingState();
    scrollElement?.addEventListener('scroll', syncDesktopFloatingState, { passive: true });
    window.addEventListener('resize', syncDesktopFloatingState);

    return () => {
      scrollElement?.removeEventListener('scroll', syncDesktopFloatingState);
      window.removeEventListener('resize', syncDesktopFloatingState);
    };
  }, [isH5]);

  // 展开态收回：桌面端悬浮框展开后，页面上下滚动或点击框区域外都收回到简易悬浮条。
  // 点击命中 Radix 传送门内容（下拉/弹层/对话框）时不收回，避免选选项时误触。
  useEffect(() => {
    if (isH5 || !desktopFormExpanded) {
      return;
    }

    const collapse = () => setDesktopFormExpanded(false);

    // 下拉延迟关闭（约 220ms）后 Radix 才把焦点还给触发器并程序化滚动，窗口需覆盖这段时间。
    const SCROLL_COLLAPSE_SUPPRESS_MS = 600;
    const isInsideFloatingSurface = (target: Element) => {
      const panel = desktopFloatingLayerRef.current;
      if (panel?.contains(target)) return true;

      // Radix Select/Popover/Dropdown 及 Dialog 通过传送门挂到 body，其内容不算「框外」。
      return Boolean(
        target.closest(
          '[data-radix-popper-content-wrapper],[role="dialog"],[role="listbox"],[role="menu"],[data-radix-portal]',
        ),
      );
    };

    const scrollElement = document.getElementById('scrollableDiv');
    const handleScroll = () => {
      // 面板内交互引发的焦点回填滚动：忽略，避免选完选项后整个框被收起。
      if (Date.now() < suppressScrollCollapseUntilRef.current) return;
      collapse();
    };

    const handlePointerDown = (event: Event) => {
      const target = event.target as Element | null;
      if (!target) return;

      if (isInsideFloatingSurface(target)) {
        suppressScrollCollapseUntilRef.current = Date.now() + SCROLL_COLLAPSE_SUPPRESS_MS;
        return;
      }

      collapse();
    };

    scrollElement?.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      scrollElement?.removeEventListener('scroll', handleScroll);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [desktopFormExpanded, isH5]);

  useLayoutEffect(() => {
    if (isH5) {
      setDesktopFormPinnedHeight(null);
      return;
    }

    const formElement = desktopFormAnchorRef.current;
    if (!formElement) {
      return;
    }

    if (desktopFormFloating) {
      setDesktopFormPinnedHeight(Math.ceil(formElement.getBoundingClientRect().height));
      return;
    }

    setDesktopFormPinnedHeight(null);
  }, [desktopFormFloating, isH5]);

  const handleExpandFloatingForm = () => {
    if (isH5) {
      setMobileFormExpanded(true);
    } else {
      setDesktopFormExpanded(true);
    }
  };

  // 完整框展开后自动聚焦输入框：两段式挂载下点击那帧面板尚未 commit，
  // 依赖 expanded 变 true 时面板已挂载，再在下一帧聚焦更稳。
  const shouldFocusExpanded = isH5 ? mobileFormExpanded : desktopFormExpanded;
  useEffect(() => {
    if (!shouldFocusExpanded) {
      return;
    }
    const focusTimer = requestAnimationFrame(() => plainPromptRef.current?.focus());
    return () => cancelAnimationFrame(focusTimer);
  }, [shouldFocusExpanded]);

  return {
    desktopExpandExiting: desktopExpandRendered && !desktopFormExpanded,
    desktopExpandRendered,
    desktopFloatingExiting: desktopFloatingRendered && !desktopFormFloating,
    desktopFloatingLayerRef,
    desktopFloatingRendered,
    desktopFormAnchorRef,
    desktopFormExpanded,
    desktopFormFloating,
    desktopFormPinnedHeight,
    elementsSelectorOpen,
    handleExpandFloatingForm,
    mobileFormExpanded,
    mobileMediaOptionsOpen,
    mobileModelOpen,
    mobileSettingsOpen,
    multiFramePromptRef,
    plainPromptRef,
    setDesktopFormExpanded,
    setDesktopFormFloating,
    setElementsSelectorOpen,
    setMobileFormExpanded,
    setMobileMediaOptionsOpen,
    setMobileModelOpen,
    setMobileSettingsOpen,
  };
};
